<script setup>
import { onMounted, ref } from 'vue';
import AOS from 'aos';

import logisticsTrainingImg from '@/assets/logisticsTraining.webp';
import identifyImg from '@/assets/identify.png';
import predictImg from '@/assets/predict.png';
import decideImg from '@/assets/decide.png';
import executeImg from '@/assets/execute.png';

const outcomes = ref([
  {
    icon: identifyImg,
    alt: 'identify',
    titleKey: 'training-page.expected-outcomes.points[0].title',
    paragraphKey: 'training-page.expected-outcomes.points[0].paragraph',
  },
  {
    icon: predictImg,
    alt: 'predict',
    titleKey: 'training-page.expected-outcomes.points[1].title',
    paragraphKey: 'training-page.expected-outcomes.points[1].paragraph',
  },
  {
    icon: decideImg,
    alt: 'decide',
    titleKey: 'training-page.expected-outcomes.points[2].title',
    paragraphKey: 'training-page.expected-outcomes.points[2].paragraph',
  },
  {
    icon: executeImg,
    alt: 'execute',
    titleKey: 'training-page.expected-outcomes.points[3].title',
    paragraphKey: 'training-page.expected-outcomes.points[3].paragraph',
  },
]);

const techniques = ref([
  { numberKey: 'training-page.driving-techniques.techniques[0].number', pointKey: 'training-page.driving-techniques.techniques[0].point' },
  { numberKey: 'training-page.driving-techniques.techniques[1].number', pointKey: 'training-page.driving-techniques.techniques[1].point' },
  { numberKey: 'training-page.driving-techniques.techniques[2].number', pointKey: 'training-page.driving-techniques.techniques[2].point' },
  { numberKey: 'training-page.driving-techniques.techniques[3].number', pointKey: 'training-page.driving-techniques.techniques[3].point' },
  { numberKey: 'training-page.driving-techniques.techniques[4].number', pointKey: 'training-page.driving-techniques.techniques[4].point' },
  { numberKey: 'training-page.driving-techniques.techniques[5].number', pointKey: 'training-page.driving-techniques.techniques[5].point' },
  { numberKey: 'training-page.driving-techniques.techniques[6].number', pointKey: 'training-page.driving-techniques.techniques[6].point' },
]);

onMounted(() => {
  AOS.init({
    duration: 800,
    once: true,
  });
});
</script>

<template>
  <div class="page-wrapper">
    <div class="hero-section">
      <div class="hero-overlay"></div>
      <h1 class="title" data-aos="fade-up">{{ $t('training-page.home.title') }}</h1>
    </div>

    <main class="main-content">
      <!-- Introduction Section -->
      <section class="section-container introduction-section">
        <div class="introduction-grid">
          <div class="image-wrapper" data-aos="fade-right">
            <img :src="logisticsTrainingImg" alt="Logistics Training" class="intro-image">
          </div>
          <div class="text-content" data-aos="fade-left" data-aos-delay="200">
            <h2 class="section-title">{{ $t('training-page.home.introduction.title') }}</h2>
            <p>{{ $t('training-page.home.introduction.text[0]') }}</p>
            <p>{{ $t('training-page.home.introduction.text[1]') }}</p>
            <p>{{ $t('training-page.home.introduction.text[2]') }}</p>
          </div>
        </div>
      </section>

      <!-- Expected Outcomes Section -->
      <section class="section-container outcomes-section">
        <div class="outcomes-grid">
          <div class="outcomes-header" data-aos="fade-right">
            <h2 class="section-title">{{ $t('training-page.expected-outcomes.header.title') }}</h2>
            <p class="section-subtitle">{{ $t('training-page.expected-outcomes.header.description') }}</p>
            <router-link to="/contact" class="btn-contact">{{ $t('training-page.expected-outcomes.header.contact-us') }}</router-link>
          </div>
          <div class="outcomes-body" data-aos="fade-left" data-aos-delay="200">
            <div v-for="(outcome, index) in outcomes" :key="index" class="point-card">
              <img :src="outcome.icon" :alt="outcome.alt" class="point-icon">
              <h3 class="point-title">{{ $t(outcome.titleKey) }}</h3>
              <p class="point-paragraph">{{ $t(outcome.paragraphKey) }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Driving Techniques Section -->
      <section class="section-container techniques-section">
        <h2 class="section-title">{{ $t('training-page.driving-techniques.title') }}</h2>
        <p class="section-subtitle">{{ $t('training-page.driving-techniques.description') }}</p>
        <div class="techniques-grid">
          <div v-for="(technique, index) in techniques" :key="index" class="technique-card" data-aos="zoom-in-up">
            <div class="technique-number">{{ $t(technique.numberKey) }}</div>
            <p class="technique-text">{{ $t(technique.pointKey) }}</p>
          </div>
        </div>
      </section>

      <!-- Final Quote Section -->
      <section class="final-quote-section">
        <div class="quote-overlay"></div>
        <h2 class="quote-text" data-aos="zoom-in">" {{ $t('training-page.end-of-line') }} "</h2>
      </section>
    </main>
  </div>
</template>

<style scoped>
.home{
    background:url("@/assets/truck-moving-front.webp") center center no-repeat;
    background-size: cover;    
    height:50vh;
    display: flex;
    justify-content: center;
    align-items: center;
    position:relative;
}
.hero-section {
  background: url("@/assets/truck-moving-front.webp") center center no-repeat;
  background-size: cover;
  height: 50vh;
  object-fit: cover;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  text-align: center;
}
.hero-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(10, 45, 97, 0.6);
}
.title{
    text-transform: uppercase;
    color: white;
    font-size: 3rem;
    position:relative;
    z-index: 2;
    padding-bottom: 0.5rem;
}
.title::after{
    content: "";
    position: absolute;
    background: #fcbc0d;
    width: 80px;
    height: 4px;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
}

.main-content {
  font-family: "Montserrat", sans-serif;
}

.section-container {
  padding: 6rem 5%;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #0A2D61;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #555;
  max-width: 800px;
  line-height: 1.7;
  margin-bottom: 3rem;
}

/* Introduction Section */
.introduction-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}
.image-wrapper {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}
.intro-image {
  width: 100%;
  height: auto;
  object-fit: cover;
  display: block;
}
.text-content {
  text-align: left;
}
.text-content p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #555;
  margin-bottom: 1rem;
}

/* Outcomes Section */
.outcomes-section {
  background-color: #f8f9fa;
}
.outcomes-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 4rem;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}
.outcomes-header {
  text-align: left;
}
.outcomes-header .section-subtitle {
  text-align: left;
  margin-left: 0;
}
.btn-contact {
  display: inline-block;
  background-color: #0A2D61;
  color: white;
  padding: 14px 28px;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  transition: background-color 0.3s ease;
  margin-top: 1rem;
}
.btn-contact:hover {
  background-color: #1c4b8d;
}
.outcomes-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}
.point-card {
  background: #fff;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
}
.point-icon {
  width: 50px;
  height: 50px;
  margin-bottom: 1rem;
}
.point-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 0.5rem 0;
}
.point-paragraph {
  font-size: 0.95rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

/* Techniques Section */
.techniques-section .section-title, .techniques-section .section-subtitle {
  text-align: center;
  margin-left: auto;
  margin-right: auto;
}
.techniques-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}
.technique-card {
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.07);
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}
.technique-number {
  font-size: 1.5rem;
  font-weight: 800;
  color: #fcbc0d;
}
.technique-text {
  font-size: 1rem;
  color: #555;
  line-height: 1.6;
  margin: 0;
}

/* Final Quote Section */
.final-quote-section {
  background-image: url("../assets/truck-back-header.webp");
  background-size: cover;
  background-position: center;
  padding: 8rem 5%;
  position: relative;
  text-align: center;
  color: white;
}
.quote-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(10, 45, 97, 0.7);
}
.quote-text {
  position: relative;
  font-size: 2.5rem;
  font-weight: 700;
  text-transform: uppercase;
  max-width: 900px;
  margin: 0 auto;
  line-height: 1.4;
}

@media (max-width: 992px) {
  .introduction-grid, .outcomes-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .title, .quote-text {
    font-size: 2rem;
  }
  .section-title {
    font-size: 2rem;
  }
  .outcomes-body {
    grid-template-columns: 1fr;
  }
}
</style>
